import { ExternalLink, Loader2, ReceiptText } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFundTransactionJournalEntries } from '@/hooks/react-query/useFundTransactions';
import type { FundTransactionGLTransaction } from '@/types/fund-transactions';
import { formatCurrency, formatDate } from '@/utils/formatters';

interface FundJournalEntriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId: string;
  transactionId: string;
}

export function FundJournalEntriesDialog({
  open,
  onOpenChange,
  fundId,
  transactionId,
}: FundJournalEntriesDialogProps) {
  const { data, isLoading, isError, error } = useFundTransactionJournalEntries(
    fundId,
    transactionId,
    open
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5" />
            Journal entries
          </DialogTitle>
          <DialogDescription>
            General ledger entries posted for this transaction.
          </DialogDescription>
        </DialogHeader>

        <JournalEntriesContent
          data={data}
          isLoading={isLoading}
          isError={isError}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}

type JournalEntriesContentProps = Pick<
  ReturnType<typeof useFundTransactionJournalEntries>,
  'data' | 'isLoading' | 'isError' | 'error'
>;

function JournalEntriesContent({
  data,
  isLoading,
  isError,
  error,
}: JournalEntriesContentProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading journal entries…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="py-8 text-sm text-destructive">
        {error?.message || 'Failed to load journal entries'}
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        <p>No journal entries have been posted for this transaction yet.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
      {data.map((group) => (
        <GLGroup key={group.id} group={group} />
      ))}
    </div>
  );
}

function GLGroup({ group }: { group: FundTransactionGLTransaction }) {
  const navigate = useNavigate();

  const handleOpenInGL = () => {
    navigate(
      `/app/funds/${group.fund_id}?tab=general-ledger&section=entries&reference=${encodeURIComponent(
        group.reference ?? group.id
      )}`
    );
  };

  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="space-y-0.5">
          <div className="text-sm font-medium">{group.fund_name}</div>
          <div className="text-xs text-muted-foreground">
            {formatDate(group.transaction_date)} ·{' '}
            {group.transaction_type.replaceAll('_', ' ')}
            {group.reference ? ` · ${group.reference}` : ''}
          </div>
          <div className="text-xs text-muted-foreground">
            {group.description}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">
            {formatCurrency(group.total_amount, { currency: group.currency })}
          </div>
          <Button variant="outline" size="sm" onClick={handleOpenInGL}>
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open in GL
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px]">Account</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right w-[140px]">Debit</TableHead>
            <TableHead className="text-right w-[140px]">Credit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.lines.map((line) => (
            <TableRow key={line.id}>
              <TableCell>
                <div className="font-semibold">{line.account_code}</div>
                <div className="text-xs text-muted-foreground">
                  {line.account_name}
                </div>
              </TableCell>
              <TableCell className="text-sm">{line.description}</TableCell>
              <TableCell className="text-right font-medium">
                {line.debit_amount > 0
                  ? formatCurrency(line.debit_amount, {
                      currency: line.currency,
                    })
                  : '-'}
              </TableCell>
              <TableCell className="text-right font-medium">
                {line.credit_amount > 0
                  ? formatCurrency(line.credit_amount, {
                      currency: line.currency,
                    })
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
